import * as THREE from 'three';
import type { Entity } from './Entity';

/**
 * Shared fire-control math. Three near-identical quadratic-intercept implementations used to
 * live in PlayerPlane, AlliedTank and TownGunner — one source of truth keeps the ballistics
 * consistent across every gun in the game.
 */

/** First-order intercept: the point to aim at so a projectile of `projSpeed` (launched from
 *  `muzzle`) meets a target currently at `base` moving with velocity `vel`. Solves
 *  |base + vel·t − muzzle| = projSpeed·t for the smallest useful positive root. Returns a clone
 *  of `base` (aim at where it is now) when no solution lands within `maxT` seconds. */
export function interceptPoint(
  muzzle: THREE.Vector3,
  base: THREE.Vector3,
  vel: THREE.Vector3,
  projSpeed: number,
  maxT = 4,
): THREE.Vector3 {
  const rel = base.clone().sub(muzzle);
  const a = vel.lengthSq() - projSpeed * projSpeed;
  const b = 2 * rel.dot(vel);
  const c = rel.lengthSq();
  let t = -1;
  if (Math.abs(a) < 1e-6) {
    if (Math.abs(b) > 1e-6) t = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      const t1 = (-b - sq) / (2 * a), t2 = (-b + sq) / (2 * a);
      t = (t1 > 0 && t2 > 0) ? Math.min(t1, t2) : Math.max(t1, t2);
    }
  }
  if (t > 0 && t < maxT) return base.clone().addScaledVector(vel, t);
  return base.clone();
}

/** Best-effort world velocity of a target: `forward * speed` when the entity exposes them
 *  (planes, tanks, bosses), else a heuristic cruise speed along `forward`, else zero.
 *  `fallbackSpeed` covers entities with a forward but no speed field. */
export function entityVelocity(target: Entity, fallbackSpeed?: number): THREE.Vector3 {
  const anyT = target as unknown as { forward?: THREE.Vector3; speed?: number };
  if (!anyT.forward) return new THREE.Vector3();
  const speed = typeof anyT.speed === 'number'
    ? anyT.speed
    : (fallbackSpeed ?? (target.kind === 'zeppelin' ? 15 : 62));
  return anyT.forward.clone().multiplyScalar(speed);
}
